import * as XLSX from "xlsx-js-style";

/* Mirrors the in-app statistics report (components/reports/GameStatistics.js):
   same palette, labels, stat cards, sections and column layout as the browser page. */

const COLORS = {
    secondary: "D2AB67", // var(--secondary-color)
    heading: "111827", // gray-900
    body: "1F2937", // gray-800
    soft: "4B5563", // gray-600
    muted: "6B7280", // gray-500
    faint: "9CA3AF", // gray-400
    border: "E5E7EB", // gray-200
    divider: "F3F4F6", // gray-100
    white: "FFFFFF",
};

const BORDER = { style: "thin", color: { rgb: COLORS.border } };
const DIVIDER = { style: "thin", color: { rgb: COLORS.divider } };
const WHITE_FILL = { fgColor: { rgb: COLORS.white } };
const BAR_WIDTH = 12;

const cell = (v, s = {}) =>
    typeof v === "number" ? { v, t: "n", z: "#,##0", s } : { v: v ?? "—", t: "s", s };

const sectionTitleStyle = {
    font: { bold: true, sz: 9, color: { rgb: COLORS.muted } },
    alignment: { vertical: "center" },
};

const headCellStyle = (alignRight) => ({
    font: { bold: true, sz: 9, color: { rgb: COLORS.muted } },
    alignment: { horizontal: alignRight ? "right" : "left", vertical: "center" },
    border: { bottom: BORDER },
    fill: WHITE_FILL,
});

const labelCellStyle = (isEmpty) => ({
    font: isEmpty
        ? { sz: 10, italic: true, color: { rgb: COLORS.faint } }
        : { sz: 10, color: { rgb: COLORS.body } },
    alignment: { vertical: "center" },
    border: { bottom: DIVIDER },
    fill: WHITE_FILL,
});

const barCellStyle = {
    font: { sz: 10, color: { rgb: COLORS.secondary } },
    alignment: { vertical: "center" },
    border: { bottom: DIVIDER },
    fill: WHITE_FILL,
};

const numCellStyle = (isPrimary) => ({
    font: isPrimary
        ? { bold: true, sz: 10, color: { rgb: COLORS.heading } }
        : { sz: 10, color: { rgb: COLORS.soft } },
    alignment: { horizontal: "right", vertical: "center" },
    border: { bottom: DIVIDER },
    fill: WHITE_FILL,
});

/* Text bar mimicking the page's share bars */
const bar = (value, max) => {
    const n = Number(value) || 0;
    if (n <= 0 || max <= 0) return "";
    return "█".repeat(Math.max(1, Math.round((n / max) * BAR_WIDTH)));
};

/* Three stat cards like the page's StatCard row */
const pushCards = (aoa, rowHeights, cards) => {
    aoa.push([]);
    rowHeights.push(8);
    aoa.push(
        cards.map((c) =>
            cell(c.label.toUpperCase(), {
                font: { bold: true, sz: 9, color: { rgb: COLORS.muted } },
                alignment: { vertical: "center" },
                border: { top: BORDER, left: BORDER, right: BORDER },
                fill: WHITE_FILL,
            })
        )
    );
    rowHeights.push(18);
    aoa.push(
        cards.map((c) =>
            cell(c.value, {
                font: { bold: true, sz: 16, color: { rgb: COLORS.heading } },
                alignment: { vertical: "center" },
                border: { bottom: BORDER, left: BORDER, right: BORDER },
                fill: WHITE_FILL,
            })
        )
    );
    rowHeights.push(30);
};

/* Section title + header row + data rows (label | share bar | values) */
const pushTable = (aoa, rowHeights, { title, headLabels, rows }) => {
    aoa.push([]);
    rowHeights.push(12);
    aoa.push([cell(title.toUpperCase(), sectionTitleStyle)]);
    rowHeights.push(16);

    aoa.push([
        cell(headLabels[0].toUpperCase(), headCellStyle(false)),
        cell("", headCellStyle(false)),
        ...headLabels.slice(1).map((l) => cell(l.toUpperCase(), headCellStyle(true))),
    ]);
    rowHeights.push(18);

    if (!rows.length) {
        aoa.push([cell("Nuk ka të dhëna për të shfaqur.", labelCellStyle(true))]);
        rowHeights.push(18);
        return;
    }

    const max = Math.max(1, ...rows.map((r) => Number(r.values[0]) || 0));
    rows.forEach((r) => {
        aoa.push([
            cell(r.label, labelCellStyle(r.isEmpty)),
            cell(bar(r.values[0], max), barCellStyle),
            ...r.values.map((v, i) => cell(v, numCellStyle(i === 0))),
        ]);
        rowHeights.push(18);
    });
};

const buildReportSheet = (wb, sheetName, { gameTitle, labels, tierRows, munRows, munName }) => {
    const totals = {
        participants: tierRows.reduce((s, i) => s + (Number(i.total_winners) || 0), 0),
        receipts: tierRows.reduce((s, i) => s + (parseInt(i.total_receipts) || 0), 0),
        communes: munRows.filter((i) => i.municipality_no !== null).length,
    };

    const aoa = [];
    const rowHeights = [];

    aoa.push([
        cell(gameTitle, {
            font: { bold: true, sz: 15, color: { rgb: COLORS.heading } },
            alignment: { vertical: "center" },
        }),
    ]);
    rowHeights.push(26);
    aoa.push([
        cell(`Raporti i statistikave — të dhënat e fundit për ${labels.subtitle} dhe kuponat.`, {
            font: { sz: 10, color: { rgb: COLORS.muted } },
            alignment: { vertical: "center" },
        }),
    ]);
    rowHeights.push(16);

    pushCards(aoa, rowHeights, [
        { label: labels.countWord, value: totals.participants },
        { label: "Kupona", value: totals.receipts },
        { label: "Komuna", value: totals.communes },
    ]);

    pushTable(aoa, rowHeights, {
        title: labels.tierTitle,
        headLabels: ["Kushti", labels.countWord, "Kupona"],
        rows: tierRows.map((item) => ({
            label: item.tier_level ? `Kushti ${item.tier_level}` : labels.emptyTier,
            isEmpty: !item.tier_level,
            values: [Number(item.total_winners) || 0, parseInt(item.total_receipts) || 0],
        })),
    });

    pushTable(aoa, rowHeights, {
        title: labels.munTitle,
        headLabels: ["Komuna", labels.countWord],
        rows: munRows.map((item) => {
            const name = munName(item.municipality_no);
            return {
                label: name || "Pa komunë",
                isEmpty: !name,
                values: [Number(item.total_winners) || 0],
            };
        }),
    });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 36 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];
    ws["!rows"] = rowHeights.map((hpt) => ({ hpt }));
    ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
};

export function exportGameStatisticsToExcel({ data, municipalities, gameTitle }) {
    if (!data) {
        alert("Nuk ka të dhëna për të eksportuar");
        return false;
    }

    const wb = XLSX.utils.book_new();
    const safeTitle = gameTitle || "Loja";
    const munName = (no) =>
        municipalities?.data?.find((m) => m.municipality_no === no)?.municipality_name;

    const datasets = [
        {
            sheetName: "Fituesit",
            tierRows: data.winners_by_tier,
            munRows: data.winners_by_municipality,
            labels: {
                countWord: "Fitues",
                tierTitle: "Fituesit sipas kushteve",
                munTitle: "Fituesit sipas komunave",
                emptyTier: "Nuk kanë arritur asnjë nivel",
                subtitle: "fituesit",
            },
        },
        {
            sheetName: "Pjesëmarrësit",
            tierRows: data.potentials_by_tier,
            munRows: data.potentials_by_municipality,
            labels: {
                countWord: "Pjesëmarrës",
                tierTitle: "Pjesëmarrësit sipas kushteve",
                munTitle: "Pjesëmarrësit sipas komunave",
                emptyTier: "Nuk kanë plotësuar asnjë kusht",
                subtitle: "pjesëmarrësit",
            },
        },
    ];

    let hasData = false;
    datasets.forEach(({ sheetName, tierRows, munRows, labels }) => {
        if (tierRows?.length || munRows?.length) {
            buildReportSheet(wb, sheetName, {
                gameTitle: safeTitle,
                labels,
                tierRows: tierRows || [],
                munRows: munRows || [],
                munName,
            });
            hasData = true;
        }
    });

    if (!hasData) {
        alert("Nuk ka të dhëna për të eksportuar");
        return false;
    }

    const timestamp = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `${safeTitle}-Statistikat-${timestamp}.xlsx`);
    return true;
}
